/**
 * Builds the per-script `ctx` SDK handed to every custom script body.
 *
 * Each script gets its OWN context with an isolated teardown registry, so one
 * script's cleanup never touches another script's nodes/listeners/observers
 * (error isolation EC7, idempotent teardown EC2). All DOM created through
 * `ctx.dom.*` and every MutationObserver is tracked and released on teardown so
 * a re-run leaves no duplicates and no leaks (EC4).
 */

// Store-write gating (9.4, AC5), driven by `script.allow_store_write`:
//  - allowWrite === true  → return the real Vuex store; dispatch/commit/set all pass
//    through (the store's own reactivity/mutation rules apply).
//  - allowWrite === false (default) → dispatch/commit are NO-OPs that log a clear
//    "write blocked" message (never silent) and return Promise.resolve(); reads
//    (state/getters/other methods) still work; the `set` trap blocks top-level direct
//    writes (e.g. `ctx.store.state.x = 1`). Deep nested mutation stays out of scope
//    (MNT-001, documented in the story Dev Notes).
const buildStoreProxy = (store, { allowWrite, log }) => {
  if (!store) return undefined;
  if (allowWrite) return store;

  return new Proxy(store, {
    get(target, prop) {
      if (prop === 'dispatch' || prop === 'commit') {
        return () => {
          // `log` already prefixes "[CustomScripts]" (defaultLogger), so the message must not
          // repeat it (MNT-002 — avoid a double prefix in the console).
          log('store write blocked (allow_store_write=false)');
          return Promise.resolve();
        };
      }
      const value = Reflect.get(target, prop);
      return typeof value === 'function' ? value.bind(target) : value;
    },
    set() {
      // Ignore direct writes (e.g. `ctx.store.state.x = 1`) when gating is on.
      return true;
    },
  });
};

export function buildScriptContext({
  script,
  surface,
  store,
  router,
  api,
  log,
  reportError,
}) {
  // LIFO registry of cleanup callbacks, plus the live nodes/observers this script
  // created. Teardown drains all three.
  const cleanups = [];
  const trackedNodes = new Set();
  const observers = new Set();

  const onCleanup = fn => {
    if (typeof fn === 'function') cleanups.push(fn);
  };

  const trackObserver = observer => {
    observers.add(observer);
    return observer;
  };

  const dom = {
    // Resolves with the first element matching `selector`, now or as soon as it is
    // added to the DOM. Uses a MutationObserver (never a setInterval, EC4); the
    // observer auto-disconnects on resolve and on teardown, so it can never leak.
    waitFor(selector, { timeout } = {}) {
      const existing = document.querySelector(selector);
      if (existing) return Promise.resolve(existing);

      return new Promise((resolve, reject) => {
        const observer = trackObserver(
          new MutationObserver(() => {
            const el = document.querySelector(selector);
            if (!el) return;
            observer.disconnect();
            observers.delete(observer);
            resolve(el);
          })
        );
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
        onCleanup(() => {
          observer.disconnect();
          observers.delete(observer);
        });

        if (timeout) {
          setTimeout(() => {
            if (!observers.has(observer)) return;
            observer.disconnect();
            observers.delete(observer);
            reject(
              new Error(`waitFor(${selector}) timed out after ${timeout}ms`)
            );
          }, timeout);
        }
      });
    },

    // Invokes `callback(element)` once for every current AND future element
    // matching `selector`. Observer auto-disconnects on teardown.
    onElement(selector, callback) {
      const seen = new WeakSet();
      const scan = () => {
        document.querySelectorAll(selector).forEach(el => {
          if (seen.has(el)) return;
          seen.add(el);
          callback(el);
        });
      };
      scan();

      const observer = trackObserver(new MutationObserver(scan));
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
      const stop = () => {
        observer.disconnect();
        observers.delete(observer);
      };
      onCleanup(stop);
      return stop;
    },

    // Creates a tracked element removed on teardown. `text`/`html` set content;
    // known props are assigned, everything else becomes an attribute.
    create(tag, props = {}) {
      const el = document.createElement(tag);
      Object.entries(props).forEach(([key, value]) => {
        if (key === 'text') el.textContent = value;
        else if (key === 'html') el.innerHTML = value;
        else if (key in el) el[key] = value;
        else el.setAttribute(key, value);
      });
      trackedNodes.add(el);
      return el;
    },

    // Appends `node` under `parent` (default <body>) and tracks it for teardown.
    inject(node, parent = document.body) {
      trackedNodes.add(node);
      parent.appendChild(node);
      return node;
    },
  };

  const teardown = () => {
    while (cleanups.length) {
      const fn = cleanups.pop();
      try {
        fn();
      } catch (error) {
        reportError(`cleanup failed for script ${script.id}`, error);
      }
    }
    observers.forEach(observer => observer.disconnect());
    observers.clear();
    trackedNodes.forEach(node => node.remove?.());
    trackedNodes.clear();
  };

  const ctx = {
    surface,
    settings: script.settings || {},
    dom,
    onCleanup,
    store: buildStoreProxy(store, {
      allowWrite: !!script.allow_store_write,
      log,
    }),
    route: router,
    api,
    log,
    reportError,
  };

  return { ctx, teardown };
}
